/**
 * Text helpers: line and word diffs (suggestion previews, guardrails policy history), and the
 * unwrap for assistant turns stored as their structured-output envelope.
 */

export type DiffLineKind = "same" | "added" | "removed";

export interface DiffLine {
  kind: DiffLineKind;
  text: string;
}

/**
 * Line-level diff of two texts (longest common subsequence), oldest-first. "removed" lines exist only
 * in `before`, "added" only in `after`. Good for a few hundred lines — skill prompts, not source files.
 */
export function diffLines(before: string, after: string): DiffLine[] {
  const a = before.length > 0 ? before.split(/\r?\n/) : [];
  const b = after.length > 0 ? after.split(/\r?\n/) : [];
  return diffSequences(a, b);
}

/**
 * Word-level diff of two single lines, for highlighting exactly what changed inside a line that the
 * line diff reports as removed + added. Whitespace is kept as its own token so joining the `text` of
 * the non-"added" parts gives back `before` (and the non-"removed" parts give back `after`).
 * Consecutive tokens of the same kind are merged.
 */
export function diffWords(before: string, after: string): DiffLine[] {
  const tokenize = (text: string) =>
    text.split(/(\s+)/).filter((t) => t.length > 0);
  const merged: DiffLine[] = [];
  for (const part of diffSequences(tokenize(before), tokenize(after))) {
    const last = merged[merged.length - 1];
    if (last && last.kind === part.kind) last.text += part.text;
    else merged.push({ ...part });
  }
  return merged;
}

/** Longest-common-subsequence diff of two token lists — shared by {@link diffLines} and {@link diffWords}. */
function diffSequences(a: string[], b: string[]): DiffLine[] {
  const n = a.length;
  const m = b.length;
  // lcs[i][j] = length of the LCS of a[i..] and b[j..]
  const lcs: number[][] = Array.from({ length: n + 1 }, () =>
    new Array<number>(m + 1).fill(0),
  );
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      lcs[i][j] =
        a[i] === b[j]
          ? lcs[i + 1][j + 1] + 1
          : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
    }
  }
  const out: DiffLine[] = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      out.push({ kind: "same", text: a[i] });
      i++;
      j++;
    } else if (lcs[i + 1][j] >= lcs[i][j + 1]) {
      out.push({ kind: "removed", text: a[i] });
      i++;
    } else {
      out.push({ kind: "added", text: b[j] });
      j++;
    }
  }
  while (i < n) out.push({ kind: "removed", text: a[i++] });
  while (j < m) out.push({ kind: "added", text: b[j++] });
  return out;
}

/**
 * The text of an assistant turn as the visitor saw it. Structured-output turns are stored as their
 * JSON envelope, `{"message":"…", …}`; older analytics artifacts quote that verbatim. Anything that is
 * not such an envelope is returned unchanged.
 */
export function readableAssistantText(content: string): string {
  const trimmed = content.trimStart();
  if (!trimmed.startsWith("{")) return content;
  try {
    const parsed: unknown = JSON.parse(trimmed);
    if (
      parsed &&
      typeof parsed === "object" &&
      typeof (parsed as { message?: unknown }).message === "string"
    ) {
      return (parsed as { message: string }).message;
    }
  } catch {
    // A quote that was cut at 200 characters is not valid JSON: salvage the message text by hand.
    const match = /^\{\s*"message"\s*:\s*"((?:[^"\\]|\\.)*)/.exec(trimmed);
    if (match) {
      try {
        return JSON.parse(`"${match[1]}"`) as string;
      } catch {
        return match[1];
      }
    }
  }
  return content;
}
